// Silent Dispatcher Mini App entry point — Phase 1 Plan 05.
//
// Initializes:
// 1. Telegram SDK (`@telegram-apps/sdk-react` init() — required for hooks/launch params).
// 2. i18next с initial locale из Telegram language_code.
// 3. AuthContext с initData raw из launchParams.
// 4. App routing (Consent / Registration / TrialActivated).

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { AppRoot } from "@telegram-apps/telegram-ui";
// Phase 12 Plan 12-00: telegram-ui global stylesheet — РОВНО ОДИН импорт до
// первого render (RESEARCH Pitfall 2). Без него все telegram-ui компоненты
// рендерятся как неоформленный HTML.
import "@telegram-apps/telegram-ui/dist/styles.css";
// Global baseline font — telegram-ui styles only the .tgui-* classes, leaving raw
// <h2>/<p>/<span> on the WKWebView serif default. Imported AFTER telegram-ui so the
// :root font-family is the document baseline; .tgui-* components keep their own
// (identical) stack via higher specificity.
import "./index.css";

import { App } from "./App";
import { initI18n, resolveTelegramLocale } from "./i18n";
import { readTelegramLaunchParams } from "./lib/telegramLaunchParams";

// Base URL — production: env-injected via Vite (same-origin
// https://dev.silentdispatcher.com — Caddy proxies /api/* to the api
// service, CORS-free by design); dev: localhost.
const API_BASE_URL =
  (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ??
  "http://localhost:8000";

async function bootstrap(): Promise<void> {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element #root not found");
  }

  const { initDataRaw, languageCode, platform, appearance } = await readTelegramLaunchParams();
  const locale = resolveTelegramLocale(languageCode);
  await initI18n(locale);

  // platform/appearance берутся из SDK launch params (НЕ хардкод — UI-SPEC
  // §Platform Contract, RESEARCH Anti-Patterns). telegram-ui AppRoot выставляет
  // --tg-theme-* переменные + platform/appearance класс на корне; старые inline
  // экраны под ним продолжают рендериться (scoped .tgui-* классы, Pattern 1).
  createRoot(rootElement).render(
    <StrictMode>
      <AppRoot platform={platform} appearance={appearance}>
        <App baseUrl={API_BASE_URL} initDataRaw={initDataRaw} />
      </AppRoot>
    </StrictMode>,
  );
}

void bootstrap();
